import {useState} from 'react';

import {useApplicantEducation} from '../hooks/useApplicantEducation';
import {useApplicantWorkExperience} from '../hooks/useApplicantWorkExperience';
import {useProfilePictureUpload} from '../hooks/useProfilePictureUpload';
import {ApplicantProfile, ProfileFormType} from '../types';

import {EditProfileForm} from './forms/EditProfileForm';
import {ProfileEditModal} from './ProfileEditModal';
import ApplicantProfileView from './view/ApplicantProfileView';
import {EducationRequestBody} from '../../../../lib/api/RequestBodies/features/applicant/profile/EducationRequestBody';
import {WorkExperienceRequestBody} from '../../../../lib/api/RequestBodies/features/applicant/profile/WorkExperienceRequestBody';
import {ApiResponseError} from '../../../../lib/api/v1/types';
import {ApplicantSkill} from '../../../../types/applicantSkill/ApplicantSkill';
import {SkillExperience} from '../../../../types/shared-types';

export interface Props {
  profile: ApplicantProfile;
  onRefetchProfile: () => void;
  onSaveSuccess: () => void;
  onSaveError: (error?: ApiResponseError) => void;
  applicantSkills: ApplicantSkill[];
  createApplicantSkills: (data: {skills: SkillExperience[]}) => void;
  onUpdateProfile: (update: Partial<ApplicantProfile>) => void;
}

export const ProfileForm = ({
  profile,
  onRefetchProfile,
  onUpdateProfile,
  onSaveError,
  onSaveSuccess,
  createApplicantSkills,
  applicantSkills,
}: Props) => {
  const [currentFormType, setCurrentFormType] = useState<{type: ProfileFormType; id?: string} | null>(null);

  const education = useApplicantEducation({
    applicantId: profile.applicantId,
    onSuccess: () => {
      onSaveSuccess();
      onRefetchProfile();
      setCurrentFormType(null);
    },
    onError: onSaveError,
  });

  const workExperience = useApplicantWorkExperience({
    applicantId: profile.applicantId,
    onSuccess: () => {
      onSaveSuccess();
      onRefetchProfile();
      setCurrentFormType(null);
    },
    onError: onSaveError,
  });

  const {saveProfilePicture} = useProfilePictureUpload({
    profile,
    onUpdateProfile,
    onSaveError,
    onSaveSuccess,
  });

  const updateProfile = (update: Partial<ApplicantProfile>) => {
    setCurrentFormType(null);
    onUpdateProfile(update);
  };

  return (
    <>
      <ProfileEditModal open={!!currentFormType}>
        <EditProfileForm
          profile={profile}
          applicantSkills={applicantSkills}
          onApplicantSkillsUpdate={(skills: {skills: SkillExperience[]}) => {
            createApplicantSkills(skills);
            setCurrentFormType(null);
          }}
          onProfileUpdate={updateProfile}
          onEducationCreate={education.create}
          onEducationUpdate={(update: EducationRequestBody) => {
            education.update(currentFormType?.id || '', update);
          }}
          onEducationDelete={education.delete}
          onWorkExperienceCreate={workExperience.create}
          onWorkExperienceUpdate={(update: WorkExperienceRequestBody) => {
            workExperience.update(currentFormType?.id || '', update);
          }}
          onWorkExperienceDelete={workExperience.delete}
          onQuit={() => {
            setCurrentFormType(null);
          }}
          currentForm={currentFormType}
        />
      </ProfileEditModal>

      <ApplicantProfileView
        data={{
          basicInfo: {
            profilePictureUrl: profile.profilePictureUrl,
            firstName: profile.firstName,
            lastName: profile.lastName,
            treatmentPronoun: profile.treatmentPronoun,
            genderPronoun: profile.genderPronoun,
            currentJobTitle:
              profile.profileTitle ||
              profile.workExperience.find((exp) => exp.isCurrent)?.roleName ||
              '',
            location: profile.location || '',
            mobilePhoneNumber: profile.mobilePhoneNumber,
            documents: profile.documents,
          },
          settings: {
            profileVisibility: profile.profileVisibility,
            jobSearchStatus: profile.jobSearchStatus,
            matchingStatus: profile.matchingStatus,
          },
          education: profile.education,
          workExperience: profile.workExperience,
          dreamJobDescription: profile.dreamJobDescription,
          skills: applicantSkills,
          languages: profile.languages,
          professionalExperience: profile.professionalExperience,
          leadershipExperience: profile.leadershipExperience,
          minSalaryValue: profile?.minSalaryValue,
          maxSalaryValue: profile?.maxSalaryValue,
          earliestStartDate: profile?.earliestStartDate,
        }}
        onImageChange={saveProfilePicture}
        onEdit={(formType, id?: string) => {
          setCurrentFormType({type: formType, id});
        }}
        refetchProfile={onRefetchProfile}
      />
    </>
  );
};
